/**
 * <product-card-quickshop>
 *
 * In-card quick-shop pattern per docs/ui-styling-guide.md §5.
 * Click 1: fetch variants from /products/<handle>.js, render size pills in place.
 * Click 2: POST selected variant to /cart/add.js, then call cart.renderContents()
 *          on Dawn's <cart-drawer> / <cart-notification> to open the drawer.
 *
 * Single-variant products are handled by Dawn's <product-form> path (not here).
 * Image link to the PDP stays untouched.
 */
class ProductCardQuickshop extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-quickshop-trigger]');
    this.label = this.querySelector('.quickshop__label');
    this.panel = this.querySelector('.quickshop__panel');
    this.handle = this.dataset.productHandle;
    this.expanded = false;
    this.loaded = false;
    this.selectedVariant = null;
    this.product = null;
    this.defaultLabel = this.label ? this.label.textContent : 'Choose options';
  }

  connectedCallback() {
    if (!this.trigger || !this.panel) return;
    this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onOutsideClick.bind(this));
  }

  async onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.expanded) {
      await this.expand();
      return;
    }

    if (this.selectedVariant) {
      await this.addToCart(this.selectedVariant);
    } else {
      this.flashPanel();
    }
  }

  async expand() {
    if (!this.loaded) {
      this.trigger.setAttribute('aria-busy', 'true');
      const ok = await this.loadVariants();
      this.trigger.removeAttribute('aria-busy');
      if (!ok) return;
    }
    this.panel.hidden = false;
    this.classList.add('is-expanded');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.expanded = true;
    this.updateLabel();
    const firstAvailable = this.panel.querySelector('input:not([disabled])');
    if (firstAvailable) firstAvailable.focus();
  }

  collapse() {
    this.panel.hidden = true;
    this.classList.remove('is-expanded');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.expanded = false;
    this.selectedVariant = null;
    this.label.textContent = this.defaultLabel;
  }

  async loadVariants() {
    try {
      const res = await fetch(`/products/${this.handle}.js`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const product = await res.json();
      this.product = product;
      this.renderPills(product.variants);
      this.loaded = true;
      return true;
    } catch (err) {
      console.warn('quickshop: failed to load variants', err);
      this.panel.innerHTML = '<p class="quickshop__error">Couldn’t load options. <a href="' + (this.dataset.productUrl || '#') + '">View product</a></p>';
      this.panel.hidden = false;
      this.classList.add('is-expanded');
      return false;
    }
  }

  renderPills(variants) {
    const sizeLabel = (this.product && this.product.options && this.product.options[0]) || 'Size';
    const html = [
      '<p class="quickshop__legend">' + this.escape(sizeLabel) + '</p>',
      '<div class="quickshop__pills">',
      ...variants.map((v, i) => this.pillHTML(v, i)),
      '</div>',
    ].join('');
    this.panel.innerHTML = html;
    this.panel.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', this.onSelect.bind(this));
    });
  }

  pillHTML(variant, index) {
    const id = `qs-${this.handle}-${variant.id}`;
    const disabled = !variant.available;
    const label = variant.option1 || variant.title || '—';
    return (
      '<label class="quickshop__pill' + (disabled ? ' is-soldout' : '') + '" for="' + id + '">' +
        '<input type="radio" id="' + id + '" name="quickshop-size-' + this.handle + '" value="' + variant.id + '"' +
        (disabled ? ' disabled' : '') + (index === 0 && !disabled ? '' : '') + '>' +
        '<span>' + this.escape(label) + '</span>' +
      '</label>'
    );
  }

  onSelect(event) {
    const id = parseInt(event.target.value, 10);
    this.selectedVariant = this.product.variants.find((v) => v.id === id);
    this.updateLabel();
  }

  updateLabel() {
    if (!this.label) return;
    if (this.selectedVariant) {
      const priceStr = this.formatMoney(this.selectedVariant.price);
      this.label.textContent = 'Add to bag — ' + priceStr;
    } else if (this.expanded) {
      this.label.textContent = 'Pick a size';
    } else {
      this.label.textContent = this.defaultLabel;
    }
  }

  async addToCart(variant) {
    const cart = document.querySelector('cart-drawer, cart-notification');
    const sections = cart && typeof cart.getSectionsToRender === 'function'
      ? cart.getSectionsToRender().map((s) => s.id)
      : undefined;
    const body = sections
      ? { items: [{ id: variant.id, quantity: 1 }], sections, sections_url: window.location.pathname }
      : { items: [{ id: variant.id, quantity: 1 }] };

    this.trigger.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        console.warn('quickshop: add to cart failed', data);
        this.label.textContent = data.description || 'Couldn’t add';
        return;
      }
      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      } else {
        // No cart drawer/notification on page — fall back to navigating to cart
        window.location.href = window.routes ? window.routes.cart_url : '/cart';
      }
      // Reset card after a short delay so user sees the success state briefly
      setTimeout(() => this.collapse(), 250);
    } catch (err) {
      console.warn('quickshop: network error adding to cart', err);
    } finally {
      this.trigger.removeAttribute('aria-busy');
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.expanded) {
      this.collapse();
      this.trigger.focus();
    }
  }

  onOutsideClick(event) {
    if (!this.expanded) return;
    if (this.contains(event.target)) return;
    this.collapse();
  }

  flashPanel() {
    this.panel.classList.add('quickshop__panel--flash');
    setTimeout(() => this.panel.classList.remove('quickshop__panel--flash'), 400);
  }

  formatMoney(cents) {
    // Shopify ships Shopify.formatMoney via shopify_common.js in some contexts but not always.
    // Use Intl as a safe default; locale follows the document.
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  escape(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('product-card-quickshop')) {
  customElements.define('product-card-quickshop', ProductCardQuickshop);
}
